import React, { useState } from 'react';
import {
  Box,
  Text,
  IconButton,
  HStack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';

const SECTIONS = [
  {
    label: 'Personal',
    items: [
      { path: '/profile', label: 'Profile', icon: 'person' },
    ],
  },
  {
    label: 'Customise',
    items: [
      { path: '/account', label: 'Account', icon: 'manage_accounts' },
      { path: '/brand', label: 'Brand', icon: 'palette' },
    ],
  },
  {
    label: 'Billing',
    items: [
      { path: '/plan-usage', label: 'Usage', icon: 'bar_chart' },
      { path: '/billing', label: 'Billing', icon: 'credit_card' },
    ],
  },
  {
    label: 'Connections',
    items: [
      { path: '/connectors', label: 'Connectors', icon: 'hub' },
    ],
  },
  {
    label: 'Legal',
    items: [
      { path: '/data-privacy', label: 'Data & Privacy', icon: 'shield' },
    ],
  },
];

const ALL_ITEMS = SECTIONS.flatMap((s) => s.items);

const SidebarContent = ({ onNavigate }) => {
  const location = useLocation();

  return (
    <Box py={5} display="flex" flexDirection="column" gap={4}>
      {SECTIONS.map((section) => (
        <Box key={section.label}>
          <Text
            fontSize="10px"
            fontWeight="700"
            color="gray.400"
            px={4}
            mb={1}
            textTransform="uppercase"
            letterSpacing="wider"
          >
            {section.label}
          </Text>
          {section.items.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path === '/connectors' && location.pathname === '/integrations');
            return (
              <Box
                key={item.path}
                onClick={() => onNavigate(item.path)}
                cursor="pointer"
                px={3}
                py={2}
                mx={2}
                display="flex"
                alignItems="center"
                gap={2}
                borderRadius="md"
                bg={isActive ? 'brand.50' : 'transparent'}
                color={isActive ? 'brand.700' : 'gray.600'}
                fontWeight={isActive ? '600' : '400'}
                fontSize="sm"
                _hover={{ bg: isActive ? 'brand.50' : 'gray.50', color: 'brand.600' }}
                transition="background 0.15s"
              >
                <span
                  className="material-icons-outlined"
                  style={{ fontSize: '17px', flexShrink: 0 }}
                >
                  {item.icon}
                </span>
                {item.label}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

const SettingsNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const currentItem =
    ALL_ITEMS.find((i) => i.path === location.pathname) ||
    (location.pathname === '/integrations'
      ? ALL_ITEMS.find((i) => i.path === '/connectors')
      : null);

  const handleNavigate = (path) => {
    setIsDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Mobile: top bar with current page + hamburger */}
      <Box
        display={{ base: 'flex', md: 'none' }}
        alignItems="center"
        justifyContent="space-between"
        px={4}
        py={3}
        borderBottom="1px solid"
        borderColor="gray.100"
      >
        <HStack spacing={2}>
          {currentItem && (
            <span
              className="material-icons-outlined"
              style={{ fontSize: '18px', color: '#718096' }}
            >
              {currentItem.icon}
            </span>
          )}
          <Text fontWeight="600" fontSize="sm" color="gray.700">
            {currentItem?.label || 'Settings'}
          </Text>
        </HStack>
        <IconButton
          aria-label="Open settings menu"
          icon={
            <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
              menu
            </span>
          }
          variant="ghost"
          size="sm"
          color="gray.500"
          _hover={{ bg: 'gray.100', color: 'gray.700' }}
          onClick={() => setIsDrawerOpen(true)}
        />
      </Box>

      {/* Mobile drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        placement="left"
        onClose={() => setIsDrawerOpen(false)}
        size="xs"
      >
        <DrawerOverlay />
        <DrawerContent maxW="240px">
          <DrawerCloseButton mt={1} />
          <Box pt={3} pb={2} px={4} borderBottom="1px solid" borderColor="gray.100">
            <Text fontWeight="700" fontSize="sm" color="gray.800">
              Settings
            </Text>
          </Box>
          <DrawerBody p={0}>
            <SidebarContent onNavigate={handleNavigate} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Desktop: vertical sidebar */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        flexDirection="column"
        w="200px"
        flexShrink={0}
        borderRight="1px solid"
        borderColor="gray.100"
      >
        <SidebarContent onNavigate={navigate} />
      </Box>
    </>
  );
};

export default SettingsNav;
